import { BISHOP, KING, KNIGHT, PAWN, QUEEN, ROOK, WHITE } from '@chess/core'
import type { Color, PieceType } from '@chess/core'
import type { JSX, ReactNode } from 'react'

/**
 * A self-contained Staunton-ish piece set drawn on a 45×45 grid.
 *
 * Flat fills with a heavy outline, in the spirit of chess.com's default set:
 * legible at 40px on a phone, which is the only size that really matters.
 * Nothing is fetched, so the app has no external asset dependency at all.
 */
const shapes: Readonly<Record<PieceType, ReactNode>> = {
  [PAWN]: (
    <>
      <circle cx="22.5" cy="13.4" r="5" />
      <path d="M22.5 18c-3.4 0-6.2 2.6-6.2 6 0 2.2 1.2 4.1 3 5.2-2.8 1.3-4.9 3.2-4.9 6h16.2c0-2.8-2.1-4.7-4.9-6 1.8-1.1 3-3 3-5.2 0-3.4-2.8-6-6.2-6z" />
      <rect x="11.4" y="34.4" width="22.2" height="4.2" rx="1.6" />
    </>
  ),

  // Facing left, with the mouth notch and the ear that make a knight legible
  // even at 40px: without them it reads as an anonymous blob.
  [KNIGHT]: (
    <>
      <path d="M22 9.2c5.5 0 9.2 3.3 10.8 8.4 1.1 3.6 1.4 7.8 1.4 12.6v3.2H12.6v-3c0-3.6 2-6.2 5.8-8.2l3-1.6-1.6-2.2-4.4 3.4c-1.3 1-3 .8-3.8-.5-.8-1.3-.4-2.9.8-3.9l5-4.2V9.2c0-1.4 1.6-1.9 2.4-.8z" />
      <circle cx="24.6" cy="15.6" r="1.3" className="fill-current" />
      <path d="M13.6 20.8h2.2" className="stroke-current" strokeWidth="1.2" fill="none" />
      <path d="M12.4 41.2c0-2.6 2.2-4.2 4.6-4.2h11c2.4 0 4.6 1.6 4.6 4.2z" />
      <rect x="9.8" y="40.2" width="25.4" height="3.6" rx="1.5" />
    </>
  ),

  [BISHOP]: (
    <>
      <circle cx="22.5" cy="7" r="2.3" />
      <path d="M22.5 9.4c4.1 3 7.1 7 7.1 11.2 0 4-3.2 6.8-7.1 6.8s-7.1-2.8-7.1-6.8c0-4.2 3-8.2 7.1-11.2z" />
      <path d="M24.8 14.4l-5.2 6.8" className="stroke-current" strokeWidth="1.7" fill="none" />
      <path d="M14.6 32.2c0-2.6 3-4.2 4.4-4.6h7c1.4.4 4.4 2 4.4 4.6z" />
      <rect x="11.5" y="31.2" width="22" height="3.8" rx="1.5" />
      <rect x="9.8" y="38.2" width="25.4" height="4.2" rx="1.6" />
      <path d="M13.4 38.2v-3.2h18.2v3.2z" />
    </>
  ),

  [ROOK]: (
    <>
      <path d="M11.6 8.6h4.8v3.4h4.1V8.6h3.9v3.4h4.1V8.6h4.9v8.8H11.6z" />
      <path d="M14.6 17.4h15.8l-1.3 13.2H15.9z" />
      <path d="M12.6 30.6h19.8v3.8H12.6z" />
      <rect x="9.8" y="34" width="25.4" height="4.4" rx="1.6" />
    </>
  ),

  [QUEEN]: (
    <>
      <circle cx="8.4" cy="12.4" r="2.4" />
      <circle cx="15.4" cy="9.4" r="2.4" />
      <circle cx="22.5" cy="8.2" r="2.6" />
      <circle cx="29.6" cy="9.4" r="2.4" />
      <circle cx="36.6" cy="12.4" r="2.4" />
      <path d="M9.4 14.4l3.4 12.4h19.4l3.4-12.4-6.2 5.2-4-9-3.5 9.6-3.5-9.6-4 9z" />
      <path d="M12.4 26.8h20.2l1.2 4.8H11.2z" />
      <rect x="9.4" y="31" width="26.2" height="4" rx="1.6" />
      <rect x="8.2" y="36.4" width="28.6" height="4.2" rx="1.7" />
      <path d="M11.6 36.4v-2.4h21.8v2.4z" />
    </>
  ),

  [KING]: (
    <>
      <path d="M21.2 5h2.6v3.4h3.4V11h-3.4v3.2h-2.6V11h-3.4V8.4h3.4z" />
      <path d="M22.5 14.6c5.6 0 10.2 3.8 10.2 8.6 0 3.4-2.2 6.4-5.4 8.2H17.7c-3.2-1.8-5.4-4.8-5.4-8.2 0-4.8 4.6-8.6 10.2-8.6z" />
      <path d="M12.8 34.6c0-2.6 3-4.2 4.6-4.6h10.2c1.6.4 4.6 2 4.6 4.6z" />
      <rect x="9.4" y="33.6" width="26.2" height="4" rx="1.6" />
      <rect x="8.2" y="39" width="28.6" height="4.2" rx="1.7" />
      <path d="M11.6 39v-2.4h21.8V39z" />
    </>
  ),
}

export interface PieceGlyphProps {
  readonly color: Color
  readonly type: PieceType
  readonly className?: string
}

/**
 * The colours come from custom properties rather than literals, which is the
 * whole of the piece-set feature: the geometry above never changes, and
 * `applyPrefs` swapping six variables restyles every piece on the page at once.
 * The fallbacks keep this correct if it renders before a theme is applied.
 */
export const PieceGlyph = ({ color, type, className }: PieceGlyphProps): JSX.Element => {
  const light = color === WHITE
  return (
    <svg
      viewBox="0 0 45 45"
      className={className}
      aria-hidden="true"
      style={{
        fill: light ? 'var(--piece-white-fill, #fcfcfa)' : 'var(--piece-black-fill, #3b3936)',
        stroke: light ? 'var(--piece-white-stroke, #4a4744)' : 'var(--piece-black-stroke, #16150f)',
        strokeWidth: 'var(--piece-stroke-width, 1.5)',
        strokeLinejoin: 'round',
        strokeLinecap: 'round',
        // `currentColor` is what the detail marks inside each shape use.
        color: light ? 'var(--piece-white-detail, #4a4744)' : 'var(--piece-black-detail, #c9c6c2)',
        filter: 'var(--piece-shadow, drop-shadow(0 1px 1px rgb(0 0 0 / 0.25)))',
      }}
    >
      {shapes[type]}
    </svg>
  )
}

export const PIECE_TYPES = [PAWN, KNIGHT, BISHOP, ROOK, QUEEN, KING] as const
